import { useState, useMemo, useCallback, useEffect } from 'react';
import { usePostApi } from '../../hooks/post_api_hook/usePostApi';
import { url } from '../../utils/confserveur';
import {
    COMPETENCES_PAR_MATIERE,
    exercicesImprimes,
    syntheseRefs,
    buildExercices,
    buildEvaluationPayload,
} from '../../utils/evalPapier';

/**
 * Panneau latéral "Évaluation de suivi" de l'aperçu PDF (2.3).
 *
 * Monté UNIQUEMENT en phase 'display' (pagination terminée) et hors
 * impression (.ep-suivi-panel masqué en @media print) : il dérive des blocs
 * FINAUX — la numérotation affichée est exactement celle du papier. Une carte
 * par exercice imprimé, une ligne par référence pédagogique dédupliquée
 * (attendu/savoir-faire), chips de compétences pré-cochées modifiables.
 *
 * Le composant est volontairement à l'écart du moteur de pagination :
 * position fixed, aucune mesure, monté après coup.
 *
 * @param {Array} blocks - blocs finaux (currentBlocks après renumberBlocks)
 * @param {Object} contexte - { idclasse, estClasse, etablissementId, matiere }
 * @param {Object} meta - printMeta (titre, date, …)
 */
const PanneauEvalSuivi = ({ blocks, contexte, meta }) => {
    const matiere = contexte?.matiere === 'français' ? 'français' : 'maths';
    const competences = COMPETENCES_PAR_MATIERE[matiere];

    // Maille de SAISIE : une carte par (exercice imprimé × ref) — le prof
    // coche les compétences exercice par exercice, comme il corrige la copie.
    const { exercices, cartes, nbHorsSuivi } = useMemo(() => {
        const resultat = exercicesImprimes(blocks, matiere);
        return {
            exercices: resultat.exercices,
            cartes: resultat.exercices.flatMap(({ exNum, refs }) =>
                refs.map((ref) => ({ exNum, ref, cle: `${exNum}:${ref.key}` }))),
            nbHorsSuivi: resultat.nbSansRef,
        };
    }, [blocks, matiere]);

    // Choix du prof, keyé `${exNum}:${refKey}` — init aux défauts au montage
    // (le panneau est démonté/remonté si la pagination repart).
    const [competencesParRef, setCompetencesParRef] = useState(() => {
        const init = {};
        cartes.forEach(({ cle, ref }) => { init[cle] = ref.compIdsParDefaut; });
        return init;
    });

    // Synthèse vivante = fusion par attendu unique (union des compétences
    // cochées) — c'est l'affichage "Ce qui sera évalué" ET le payload.
    const synthese = useMemo(
        () => syntheseRefs(exercices, competencesParRef),
        [exercices, competencesParRef]
    );

    // Lookups id compétence → nom / rang du référentiel (pour la synthèse)
    const nomsCompetences = useMemo(
        () => new Map(competences.map((c) => [c.id, c.nom])),
        [competences]
    );
    const ordreCompetences = useMemo(
        () => new Map(competences.map((c, i) => [c.id, i])),
        [competences]
    );

    const [creee, setCreee] = useState(false);
    const [{ isLoading }, doPost] = usePostApi(url + 'evaluations/');
    const [erreur, setErreur] = useState(null);

    // Nudge anti-oubli : le navigateur émet `afterprint` à la fermeture du
    // dialogue d'impression — si le prof a imprimé SANS créer l'évaluation,
    // le statut passe en alerte rouge au moment exact où l'oubli se produit.
    // (Abonnement à un événement externe : le cas d'usage légitime de useEffect.)
    const [imprimeSansCreer, setImprimeSansCreer] = useState(false);
    useEffect(() => {
        if (creee) return undefined;
        const onAfterPrint = () => setImprimeSansCreer(true);
        window.addEventListener('afterprint', onAfterPrint);
        return () => window.removeEventListener('afterprint', onAfterPrint);
    }, [creee]);

    const toggleCompetence = useCallback((cle, compId) => {
        setCompetencesParRef((prev) => {
            const actuels = prev[cle] || [];
            const suivants = actuels.includes(compId)
                ? actuels.filter((id) => id !== compId)
                : [...actuels, compId];
            return { ...prev, [cle]: suivants };
        });
    }, []);

    // La contrainte porte sur la SYNTHÈSE : un exercice peut être entièrement
    // décoché (il sort du suivi), mais il faut qu'au moins un attendu reste.
    const peutCreer = !creee && !isLoading
        && synthese.length > 0 && (meta?.titre || '').trim().length > 0;

    const handleCreer = async () => {
        if (!peutCreer) return;
        setErreur(null);
        const payload = buildEvaluationPayload({
            titre: meta.titre.trim(),
            date: meta.date,
            idclasse: contexte.idclasse,
            estClasse: contexte.estClasse,
            etablissementId: contexte.etablissementId,
            exercices: buildExercices(synthese),
        });
        const result = await doPost(payload);
        if (result?.success) {
            setCreee(true);
        } else {
            setErreur("Échec de la création — vérifie ta connexion puis réessaie.");
        }
    };

    if (cartes.length === 0) return null;

    return (
        <aside className="ep-suivi-panel">
            {/* En-tête sticky : le bouton de création et le statut restent
                visibles quel que soit le défilement des cartes. */}
            <div className="ep-suivi-entete">
                <h3 className="ep-suivi-titre">Évaluation de suivi</h3>
                {creee ? (
                    <div className="ep-suivi-succes">
                        ✓ Évaluation créée — saisie des notes disponible dans «&nbsp;Évaluations&nbsp;».
                    </div>
                ) : (
                    <>
                        <button
                            type="button"
                            className="ep-suivi-creer"
                            disabled={!peutCreer}
                            onClick={handleCreer}
                        >
                            {isLoading ? 'Création…' : "Créer l'évaluation de suivi"}
                        </button>
                        <div className={`ep-suivi-statut${imprimeSansCreer ? ' ep-suivi-statut-alerte' : ''}`}>
                            {imprimeSansCreer
                                ? "⚠ Tu as imprimé sans créer l'évaluation de suivi — crée-la maintenant pour pouvoir saisir les notes."
                                : 'Pas encore créée — sans elle, pas de saisie de notes après correction.'}
                        </div>
                        {erreur && <p className="ep-suivi-erreur">{erreur}</p>}
                    </>
                )}
            </div>

            <div className="ep-suivi-corps">
            <p className="ep-suivi-soustitre">
                Choisis les compétences évaluées pour chaque exercice
            </p>

            {/* Une carte par (exercice imprimé × attendu) : le prof coche les
                compétences exercice par exercice, comme il corrige la copie.
                La fusion par attendu est visible dans "Ce qui sera évalué". */}
            {cartes.map(({ exNum, ref, cle }) => {
                const choisis = competencesParRef[cle] || [];
                return (
                    <div key={cle} className="ep-suivi-carte">
                        <div className="ep-suivi-ref-libelle">
                            <strong>Exercice {exNum}</strong> · {ref.libelle}
                            <span className="ep-suivi-ref-badge">
                                {ref.nbQuestions} question{ref.nbQuestions > 1 ? 's' : ''}
                            </span>
                        </div>
                        <div className="ep-suivi-chips">
                            {competences.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    disabled={creee}
                                    onClick={() => toggleCompetence(cle, c.id)}
                                    className={`ep-suivi-chip${choisis.includes(c.id) ? ' ep-suivi-chip-on' : ''}`}
                                >
                                    {c.nom}
                                </button>
                            ))}
                        </div>
                        {choisis.length === 0 && (
                            <p className="ep-suivi-avertissement">
                                Aucune compétence — cet exercice ne comptera pas dans le suivi.
                            </p>
                        )}
                    </div>
                );
            })}

            {/* Synthèse vivante : la fusion par attendu unique — exactement ce
                qui sera créé en base (une ligne de saisie par attendu, union
                des compétences cochées sur ses exercices). */}
            <div className="ep-suivi-synthese">
                <div className="ep-suivi-synthese-titre">Ce qui sera évalué</div>
                {synthese.length === 0 ? (
                    <p className="ep-suivi-avertissement">
                        Aucune compétence cochée — rien ne sera suivi.
                    </p>
                ) : (
                    <>
                        <ul className="ep-suivi-synthese-liste">
                            {synthese.map((s) => {
                                // Compétences dans l'ordre du référentiel (pas l'ordre de cochage)
                                const compsTriees = [...s.comps].sort(
                                    (a, b) => (ordreCompetences.get(a.id) ?? 99) - (ordreCompetences.get(b.id) ?? 99)
                                );
                                return (
                                    <li key={s.key} className="ep-suivi-synthese-ligne">
                                        <strong>{s.libelle}</strong>
                                        <div className="ep-suivi-synthese-comps">
                                            {compsTriees.map((c) => (
                                                <span key={c.id} className="ep-suivi-synthese-comp">
                                                    {nomsCompetences.get(c.id) || c.id}
                                                    <span className="ep-suivi-synthese-ex">
                                                        Ex {c.exNums.join(', ')}
                                                    </span>
                                                </span>
                                            ))}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>         
                    </>
                )}
            </div>

            {nbHorsSuivi > 0 && (
                <p className="ep-suivi-note">
                    {nbHorsSuivi} question{nbHorsSuivi > 1 ? 's' : ''} sans attendu rattaché :
                    imprimée{nbHorsSuivi > 1 ? 's' : ''}, mais hors suivi des acquisitions.
                </p>
            )}
            </div>
        </aside>
    );
};

export default PanneauEvalSuivi;
